import {
  FillerSlideLibrary as RawFillerSlideAsset,
  retrieveFillerSlideLibrary,
  destroyFillerSlideLibrary,
  partialUpdateFillerSlideLibrary,
  createFillerSlideLibrary
} from '@goldcast/api/backend';

import store from '@/store';
import { getEnv } from '@/utils/env';
import { objectToQueryParams } from '@/utils/url';

const FillerSlideState = {
  PROCESSING: 'PROCESSING',
  NOT_GENERATED: 'NOT_GENERATED',
  STOPPED: 'STOPPED',
  GENERATED: 'GENERATED',
  ERRORED: 'ERRORED'
};

export type FillerSlideAsset = {
  id: string;
  name: string;
  urls: {
    image: string;
    video: string;
  };
  status: keyof typeof FillerSlideState;
  broadcast_filler_image_s3_path: string;
};

function transformRawFillerSlideAsset(rawFillerSlideAsset: RawFillerSlideAsset): FillerSlideAsset {
  const cloudfrontBaseUrl = getEnv('VUE_APP_CLOUDFRONT_URL');
  return {
    id: rawFillerSlideAsset.id,
    name: rawFillerSlideAsset.file_name,
    urls: {
      image: `${cloudfrontBaseUrl}/${rawFillerSlideAsset.broadcast_filler_image_s3_path}`,
      video: `${cloudfrontBaseUrl}/${rawFillerSlideAsset.broadcast_filler_video_s3_path}`
    },
    status: rawFillerSlideAsset.broadcast_filler_state || 'PROCESSING',
    broadcast_filler_image_s3_path: rawFillerSlideAsset.broadcast_filler_image_s3_path
  };
}

export function getFillerSlideAssets(params: {
  search?: string;
  offset?: number;
  limit?: number;
}): Promise<{ totalCount: number; assets: FillerSlideAsset[] }> {
  return new Promise((resolve, reject) => {
    const url = `/core/filler-slide-library/?${objectToQueryParams({
      ...params,
      ordering: '-created_at',
      uploaded: true
    })}`;

    store
      .dispatch('makeapicall', { url, method: 'GET' }, { root: true })
      .then(res => {
        if (res.count) {
          resolve({
            totalCount: res.count,
            assets: res.results.map(transformRawFillerSlideAsset)
          });
        } else if (res.length) {
          resolve({
            totalCount: res.length,
            assets: res.map(transformRawFillerSlideAsset)
          });
        } else {
          resolve({
            totalCount: 0,
            assets: []
          });
        }
      })
      .catch(reject);
  });
}

export function getFillerSlideAsset(id: string): Promise<FillerSlideAsset> {
  return new Promise((resolve, reject) => {
    retrieveFillerSlideLibrary({
      id
    })
      .then(res => resolve(transformRawFillerSlideAsset(res)))
      .catch(reject);
  });
}

export function deleteFillerSlideAsset(id: string): Promise<any> {
  return new Promise((resolve, reject) => {
    destroyFillerSlideLibrary({
      id
    })
      .then(resolve)
      .catch(error => {
        reject(error?.message || 'Something went wrong. Please try again.');
      });
  });
}

export function renameFillerSlideAsset(id: string, name: string): Promise<any> {
  return new Promise((resolve, reject) => {
    partialUpdateFillerSlideLibrary({
      id,
      body: {
        file_name: name
      }
    })
      .then(res => resolve(transformRawFillerSlideAsset(res)))
      .catch(reject);
  });
}

export function uploadFillerSlide(details: {
  fileName: string;
  organizationId: string;
  broadcastFillerImageS3Path: string;
  workspace: string | null;
}): Promise<FillerSlideAsset> {
  return new Promise((resolve, reject) => {
    createFillerSlideLibrary({
      body: {
        file_name: details.fileName,
        organization: details.organizationId,
        broadcast_filler_image_s3_path: details.broadcastFillerImageS3Path,
        workspace: details.workspace
      }
    })
      .then(rawFillerSlideAsset => {
        resolve(transformRawFillerSlideAsset(rawFillerSlideAsset));
      })
      .catch(reject);
  });
}
